import { computed, ref, watch, type Ref } from 'vue';
import { useRoute } from 'vue-router';
import {
  trainingCollaboratorApi,
  type ListTrainingCollaboratorsParams,
  type MemberTrainingStatus,
  type TrainingCollaboratorFilters,
  type TrainingCollaboratorListItem,
  type TrainingCollaboratorListMetadata,
  type TrainingCollaboratorSearchType,
  type TrainingCollaboratorSortField,
  type TrainingCollaboratorSortOrder,
} from '@/entities/Training';
import { useCustomToast, useInfiniteScrollObserver, useListUrlSync } from '@/shared/lib';
import { SHOW_ALL_ITEMS_QUANTITY } from '@/shared/config';
import {
  COLLABORATORS_TAB_ID,
  COLLABORATOR_QUANTITIES,
  COLLABORATOR_SEARCH_TYPES,
  COLLABORATOR_SORT_FIELDS,
  DEFAULT_COLLABORATOR_SEARCH_TYPE,
  DEFAULT_ITEMS_PER_PAGE,
  parseCollaboratorFilters,
} from '../lib/collaborators-list-query';
import { COLLABORATOR_ORDER_OPTIONS } from './useTrainingCollaboratorsTable';
import { useChangeCollaboratorStatus } from './useChangeCollaboratorStatus';

const ALL_ITEMS_BATCH_SIZE = 30;

const DEFAULT_SORT: TrainingCollaboratorSortField = 'name';
const DEFAULT_ORDER: TrainingCollaboratorSortOrder = 'asc';

const ERROR_MESSAGE = 'Não foi possível carregar os colaboradores. Tente novamente.';
const LOAD_MORE_ERROR_MESSAGE =
  'Não foi possível carregar mais colaboradores. Tente novamente.';

interface ResolvedOrderOption {
  sort: TrainingCollaboratorSortField;
  order: TrainingCollaboratorSortOrder;
}

function resolveOrderOption(sort: string, order: string): ResolvedOrderOption | null {
  const matched = COLLABORATOR_ORDER_OPTIONS.find(
    (option) => option.sort === sort && option.order === order
  );

  if (!matched) return null;

  return {
    sort: matched.sort as TrainingCollaboratorSortField,
    order: matched.order as TrainingCollaboratorSortOrder,
  };
}

/**
 * Single owner of the collaborators tab state.
 *
 * `useListUrlSync` answers for term, search field, ordering, page and quantity per
 * page, both when hydrating and when writing the URL. Only the status/position
 * filters are born here, hydrated from the query itself. The ordering starts unset
 * on purpose: the alphabetical default belongs to the request, not to the button,
 * which must open unmarked.
 *
 * @param reloadToken Bumped by the page when the training is completed. It joins the
 *   same watcher as the query state instead of getting one of its own, so a bump
 *   refetches with the current page, term, filters and ordering — the completion
 *   response carries only the unpaginated trainees, so patching from it would
 *   desync them.
 */
function patchItemStatus(
  list: TrainingCollaboratorListItem[],
  id: number,
  status: MemberTrainingStatus
): TrainingCollaboratorListItem[] {
  return list.map((item) => (item.id === id ? { ...item, status } : item));
}

export function useTrainingCollaborators(
  trainingId: Ref<number | null>,
  reloadToken?: Ref<number>
) {
  const { showErrorToast } = useCustomToast();
  const { changeStatus: performChangeStatus } = useChangeCollaboratorStatus();
  const route = useRoute();

  const { searchTerm, searchFieldId, sort, order, currentPage, itemsPerPage, syncUrl } =
    useListUrlSync({
      fields: COLLABORATOR_SEARCH_TYPES,
      defaultField: DEFAULT_COLLABORATOR_SEARCH_TYPE,
      fieldQueryKey: 'searchType',
      validSorts: COLLABORATOR_SORT_FIELDS,
      quantity: { options: COLLABORATOR_QUANTITIES, default: DEFAULT_ITEMS_PER_PAGE },
    });

  const initialFilters = parseCollaboratorFilters(route.query);

  const items = ref<TrainingCollaboratorListItem[]>([]);
  const allItems = ref<TrainingCollaboratorListItem[]>([]);
  const metadata = ref<TrainingCollaboratorListMetadata | null>(null);
  const loading = ref(false);
  const loadingMore = ref(false);
  const hasMoreItems = ref(true);
  const error = ref<string | null>(null);
  const status = ref<MemberTrainingStatus | undefined>(initialFilters.status);
  const positionId = ref<number | undefined>(initialFilters.positionId);
  const changingStatusIds = ref<Set<number>>(new Set());

  let currentQueryId = 0;
  let allItemsPage = 1;
  let loadMoreFailed = false;

  const isAllItemsMode = computed(() => itemsPerPage.value === SHOW_ALL_ITEMS_QUANTITY);
  const displayedItems = computed(() => (isAllItemsMode.value ? allItems.value : items.value));
  const hasCollaborators = computed(() => displayedItems.value.length > 0);
  const hasActiveSearch = computed(() => searchTerm.value.length > 0);

  const filters = computed<TrainingCollaboratorFilters>(() => ({
    ...(status.value ? { status: status.value } : {}),
    ...(positionId.value === undefined ? {} : { positionId: positionId.value }),
  }));

  const hasActiveFilters = computed(() => Object.keys(filters.value).length > 0);
  const isInitialLoading = computed(() => loading.value && metadata.value === null);
  const showControls = computed(
    () =>
      hasCollaborators.value ||
      hasActiveSearch.value ||
      hasActiveFilters.value ||
      currentPage.value > 1
  );

  function buildQueryParams(
    pageToUse: number,
    itemsPerPageToUse: number
  ): ListTrainingCollaboratorsParams {
    return {
      page: pageToUse,
      itemsPerPage: itemsPerPageToUse,
      // `||` (not `??`) is intentional: an empty string means "no explicit order", which must fall
      // back to the alphabetical default without marking the sort button.
      sort: (sort.value || DEFAULT_SORT) as TrainingCollaboratorSortField,
      order: (order.value || DEFAULT_ORDER) as TrainingCollaboratorSortOrder,
      ...(searchTerm.value
        ? {
            term: searchTerm.value,
            searchType: searchFieldId.value as TrainingCollaboratorSearchType,
          }
        : {}),
      ...filters.value,
    };
  }

  function syncListUrl() {
    syncUrl({
      tab: COLLABORATORS_TAB_ID,
      ...(status.value ? { status: status.value } : {}),
      ...(positionId.value === undefined ? {} : { positionId: positionId.value }),
    });
  }

  async function fetch() {
    const id = trainingId.value;
    if (id === null) return;

    const queryId = ++currentQueryId;
    loading.value = true;
    error.value = null;
    loadMoreFailed = false;

    try {
      if (isAllItemsMode.value) {
        allItemsPage = 1;
        const result = await trainingCollaboratorApi.listTrainingCollaborators(
          id,
          buildQueryParams(allItemsPage, ALL_ITEMS_BATCH_SIZE)
        );
        if (queryId !== currentQueryId) return;
        items.value = result.data;
        allItems.value = result.data;
        metadata.value = result.metadata;
        hasMoreItems.value = allItems.value.length < (result.metadata?.total ?? 0);
      } else {
        allItems.value = [];
        hasMoreItems.value = true;
        const result = await trainingCollaboratorApi.listTrainingCollaborators(
          id,
          buildQueryParams(currentPage.value, itemsPerPage.value)
        );
        if (queryId !== currentQueryId) return;
        items.value = result.data;
        metadata.value = result.metadata;
      }
    } catch {
      if (queryId !== currentQueryId) return;
      items.value = [];
      allItems.value = [];
      metadata.value = null;
      hasMoreItems.value = false;
      error.value = ERROR_MESSAGE;
    } finally {
      if (queryId === currentQueryId) {
        loading.value = false;
      }
    }
  }

  async function loadMore() {
    const id = trainingId.value;
    if (id === null) return;
    if (!isAllItemsMode.value) return;
    if (loading.value || loadingMore.value || !hasMoreItems.value || loadMoreFailed) return;

    const queryId = currentQueryId;
    loadingMore.value = true;

    try {
      const nextPage = allItemsPage + 1;
      const result = await trainingCollaboratorApi.listTrainingCollaborators(
        id,
        buildQueryParams(nextPage, ALL_ITEMS_BATCH_SIZE)
      );
      if (queryId !== currentQueryId) return;
      allItemsPage = nextPage;
      allItems.value = [...allItems.value, ...result.data];
      metadata.value = result.metadata;
      hasMoreItems.value = allItems.value.length < (result.metadata?.total ?? 0);
    } catch {
      if (queryId !== currentQueryId) return;
      loadMoreFailed = true;
      showErrorToast({ title: LOAD_MORE_ERROR_MESSAGE, type: 'error' });
    } finally {
      if (queryId === currentQueryId) {
        loadingMore.value = false;
      }
    }
  }

  function changePage(targetPage: number) {
    if (targetPage === currentPage.value) return;
    currentPage.value = targetPage;
  }

  function changeItemsPerPage(value: number) {
    if (value === itemsPerPage.value) return;
    itemsPerPage.value = value;
    currentPage.value = 1;
    allItems.value = [];
    hasMoreItems.value = true;
  }

  function handleSearch(newTerm: string) {
    const trimmed = newTerm.trim();
    if (trimmed === searchTerm.value) return;
    searchTerm.value = trimmed;
    currentPage.value = 1;
  }

  function handleSearchFieldChange(newSearchField: string) {
    const resolved = COLLABORATOR_SEARCH_TYPES.includes(
      newSearchField as TrainingCollaboratorSearchType
    )
      ? (newSearchField as TrainingCollaboratorSearchType)
      : DEFAULT_COLLABORATOR_SEARCH_TYPE;
    if (resolved === searchFieldId.value) return;
    searchFieldId.value = resolved;
    currentPage.value = 1;
  }

  function handleSortUpdate(newSort: string, newOrder: string) {
    if (newSort === '' && newOrder === '') {
      if (sort.value === '' && order.value === '') return;
      sort.value = '';
      order.value = '';
      currentPage.value = 1;
      return;
    }

    const selected = resolveOrderOption(newSort, newOrder);
    if (!selected) return;
    if (selected.sort === sort.value && selected.order === order.value) return;
    sort.value = selected.sort;
    order.value = selected.order;
    currentPage.value = 1;
  }

  function applyFilters(next: TrainingCollaboratorFilters = {}) {
    const nextStatus = next.status;
    const nextPositionId = next.positionId;

    if (nextStatus === status.value && nextPositionId === positionId.value) return;

    status.value = nextStatus;
    positionId.value = nextPositionId;
    currentPage.value = 1;
    allItems.value = [];
    hasMoreItems.value = true;
  }

  function isChangingStatus(id: number): boolean {
    return changingStatusIds.value.has(id);
  }

  async function changeStatus(id: number, newStatus: MemberTrainingStatus): Promise<void> {
    const trainingIdValue = trainingId.value;
    if (trainingIdValue === null || changingStatusIds.value.has(id)) return;

    changingStatusIds.value = new Set(changingStatusIds.value).add(id);
    try {
      const updated = await performChangeStatus(trainingIdValue, id, newStatus);
      if (updated) {
        items.value = patchItemStatus(items.value, id, updated.status);
        allItems.value = patchItemStatus(allItems.value, id, updated.status);
      }
    } finally {
      const next = new Set(changingStatusIds.value);
      next.delete(id);
      changingStatusIds.value = next;
    }
  }

  useInfiniteScrollObserver({
    enabled: isAllItemsMode,
    loadingMore,
    hasMore: hasMoreItems,
    onLoadMore: loadMore,
  });

  watch(
    [
      trainingId,
      currentPage,
      itemsPerPage,
      searchTerm,
      searchFieldId,
      sort,
      order,
      status,
      positionId,
      () => reloadToken?.value,
    ],
    () => {
      syncListUrl();
      void fetch();
    },
    { immediate: true }
  );

  return {
    items,
    allItems,
    metadata,
    loading,
    loadingMore,
    hasMoreItems,
    error,
    page: currentPage,
    itemsPerPage,
    term: searchTerm,
    searchField: searchFieldId,
    sort,
    order,
    status,
    positionId,
    filters,
    hasActiveFilters,
    isAllItemsMode,
    hasCollaborators,
    hasActiveSearch,
    isInitialLoading,
    showControls,
    changePage,
    changeItemsPerPage,
    handleSearch,
    handleSearchFieldChange,
    handleSortUpdate,
    applyFilters,
    loadMore,
    fetch,
    changeStatus,
    isChangingStatus,
  };
}
